<template>
  <div
    class="autocomplete"
    :class="{
      'is-open': isOpen,
      'is-disabled': disabled,
      'has-error': showSelectionError
    }"
  >
    <input
      ref="input"
      class="autocomplete__input input-default w-100"
      type="text"
      autocomplete="off"
      role="combobox"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-invalid="showSelectionError ? 'true' : 'false'"
      :aria-describedby="showSelectionError ? errorId : null"
      v-model.trim="query"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="chooseHighlighted"
      @keydown.esc.prevent="close"
    >
    <div v-if="isOpen && suggestions.length" class="autocomplete__list" role="listbox">
      <div
        v-for="(option, index) in suggestions"
        :key="getKey(option, index)"
        class="autocomplete__item"
        :class="{ 'is-active': index === highlightIndex }"
        role="option"
        :aria-selected="index === highlightIndex ? 'true' : 'false'"
        @mousedown.prevent="selectOption(option)"
      >
        {{ getLabel(option) }}
      </div>
    </div>
    <div v-else-if="showNoResults" class="autocomplete__list autocomplete__empty">
      {{ noResultsText }}
    </div>
    <p
      v-if="showSelectionError"
      :id="errorId"
      class="autocomplete__error italic color-red2 font-smoothing fs-12 mb-2"
    >
      {{ selectionErrorText }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'AutocompleteSelect',
  props: {
    value: null,
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    label: {
      type: Function,
      default: null
    },
    trackBy: {
      type: String,
      default: ''
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxSuggestions: {
      type: Number,
      default: 20
    },
    minChars: {
      type: Number,
      default: 2
    },
    showSuggestionsOnFocus: {
      type: Boolean,
      default: false
    },
    requireSelection: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    selectionErrorText: {
      type: String,
      default: 'Vælg en mulighed fra listen.'
    },
    noResultsText: {
      type: String,
      default: 'Ingen resultater. Prøv et andet navn eller en kode.'
    }
  },
  data() {
    return {
      query: '',
      isOpen: false,
      suggestions: [],
      highlightIndex: -1,
      buildTimer: null,
      buildIdleHandle: null,
      buildQueued: false,
      searchTimer: null,
      idleHandle: null,
      searchPending: false,
      index: [],
      iataIndex: Object.create(null),
      selectionErrorVisible: false,
      preserveQueryOnClear: false,
      errorId: `autocomplete-error-${this._uid}`
    };
  },
  computed: {
    showSelectionError() {
      if (this.value) return false;
      return this.invalid || (
        this.requireSelection
        && this.selectionErrorVisible
      );
    },
    showNoResults() {
      return this.isOpen
        && !this.searchPending
        && this.normalize(this.query).length >= this.minChars
        && this.suggestions.length === 0;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) {
          if (this.preserveQueryOnClear) {
            this.preserveQueryOnClear = false;
            return;
          }
          if (this.allowEmpty) {
            this.query = '';
          }
          return;
        }
        this.query = this.getLabel(newVal);
        this.selectionErrorVisible = false;
      },
      immediate: true
    },
    options: {
      handler() {
        this.scheduleBuildIndex();
      },
      deep: false,
      immediate: true
    }
  },
  methods: {
    getLabel(option) {
      if (!option) return '';
      if (this.label) return this.label(option);
      if (typeof option === 'string') return option;
      return option.label || option.name || '';
    },
    getKey(option, index) {
      if (this.trackBy && option && option[this.trackBy] != null) {
        return option[this.trackBy];
      }
      return index;
    },
    onInput() {
      if (this.value && this.query !== this.getLabel(this.value)) {
        this.preserveQueryOnClear = true;
        this.$emit('input', null);
        this.$emit('clear');
      }
      this.selectionErrorVisible = false;
      this.suggestions = [];
      this.highlightIndex = -1;
      this.$emit('query-change', this.query);
      this.scheduleSearch();
      this.open();
    },
    onFocus() {
      this.open();
      if (this.showSuggestionsOnFocus) {
        this.updateSuggestions(true);
        this.searchPending = false;
        return;
      }
      this.scheduleSearch();
    },
    onBlur() {
      const hasInvalidFreeText = !!this.query && !this.value;
      const isMissingRequiredValue = !this.allowEmpty && !this.value;

      if (this.requireSelection && (hasInvalidFreeText || isMissingRequiredValue)) {
        this.selectionErrorVisible = true;
        this.$emit('invalid-selection', this.query);
      }
      setTimeout(() => {
        this.close();
      }, 100);
      if (!this.query && this.allowEmpty) {
        this.$emit('input', null);
      }
    },
    open() {
      if (!this.disabled) {
        this.isOpen = true;
      }
    },
    close() {
      this.isOpen = false;
      this.highlightIndex = -1;
    },
    move(delta) {
      if (!this.suggestions.length) return;
      const next = this.highlightIndex + delta;
      if (next < 0) {
        this.highlightIndex = this.suggestions.length - 1;
      } else if (next >= this.suggestions.length) {
        this.highlightIndex = 0;
      } else {
        this.highlightIndex = next;
      }
    },
    chooseHighlighted() {
      const query = this.normalize(this.query);
      if (query.length < this.minChars) return;

      if (!this.suggestions.length) {
        if (!this.index.length && this.getSearchableOptions().length) {
          this.buildIndex();
        }
        this.updateSuggestions();
        this.searchPending = false;
      }
      if (this.highlightIndex >= 0 && this.highlightIndex < this.suggestions.length) {
        this.selectOption(this.suggestions[this.highlightIndex]);
      } else if (this.suggestions.length) {
        this.selectOption(this.suggestions[0]);
      }
    },
    selectOption(option) {
      this.query = this.getLabel(option);
      this.selectionErrorVisible = false;
      this.searchPending = false;
      this.$emit('input', option);
      this.$emit('select', option);
      this.close();
    },
    scheduleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      if (this.idleHandle && window.cancelIdleCallback) {
        window.cancelIdleCallback(this.idleHandle);
        this.idleHandle = null;
      }
      this.searchPending = true;
      const run = () => {
        this.updateSuggestions();
        this.searchPending = false;
      };
      if (window.requestIdleCallback) {
        this.idleHandle = window.requestIdleCallback(run, { timeout: 400 });
      } else {
        this.searchTimer = setTimeout(run, 250);
      }
    },
    scheduleBuildIndex() {
      if (this.buildQueued) return;
      this.buildQueued = true;
      if (this.buildTimer) {
        clearTimeout(this.buildTimer);
        this.buildTimer = null;
      }
      if (this.buildIdleHandle && window.cancelIdleCallback) {
        window.cancelIdleCallback(this.buildIdleHandle);
        this.buildIdleHandle = null;
      }
      const run = () => {
        this.buildQueued = false;
        this.buildIndex();
      };
      if (window.requestIdleCallback) {
        this.buildIdleHandle = window.requestIdleCallback(run, { timeout: 800 });
      } else {
        this.buildTimer = setTimeout(run, 0);
      }
    },
    normalize(str) {
      return (str || '').toString().toLowerCase();
    },
    isSearchHidden(option) {
      return option && typeof option === 'object' && option.hideFromAutocomplete === true;
    },
    getSearchableOptions() {
      const opts = Array.isArray(this.options) ? this.options : [];
      return opts.filter((option) => !this.isSearchHidden(option));
    },
    addToBucket(buckets, prefix, option) {
      if (!buckets[prefix]) buckets[prefix] = [];
      if (!buckets[prefix].includes(option)) {
        buckets[prefix].push(option);
      }
    },
    buildIndex() {
      const opts = this.getSearchableOptions();
      this.index = opts.map((option) => {
        const label = this.getLabel(option);
        const iata = option && option.iata ? this.normalize(option.iata) : '';
        const aliases = option && Array.isArray(option.aliases)
          ? option.aliases.map((alias) => this.normalize(alias)).filter(Boolean)
          : [];
        const extra = option && typeof option === 'object'
          ? `${option.name || ''} ${option.cityDK || ''} ${option.countryDK || ''} ${aliases.join(' ')}`
          : '';
        const searchText = this.normalize(`${label} ${iata} ${extra}`);
        return { option, searchText, iata, aliases };
      });
      const buckets = Object.create(null);
      for (let i = 0; i < this.index.length; i += 1) {
        const codes = [this.index[i].iata, ...this.index[i].aliases].filter(Boolean);
        for (let codeIndex = 0; codeIndex < codes.length; codeIndex += 1) {
          const code = codes[codeIndex];
          for (let len = 1; len <= 3 && len <= code.length; len += 1) {
            const prefix = code.slice(0, len);
            this.addToBucket(buckets, prefix, this.index[i].option);
          }
        }
      }
      this.iataIndex = buckets;
    },
    updateSuggestions(forceOpenSuggestions = false) {
      const query = this.normalize(this.query);
      if (query.length < this.minChars) {
        if (forceOpenSuggestions && this.showSuggestionsOnFocus) {
          this.suggestions = this.getSearchableOptions().slice(0, this.maxSuggestions);
          this.highlightIndex = -1;
          return;
        }
        this.suggestions = [];
        return;
      }
      const results = [];
      const onlySimple = query.replace(/[^a-z0-9]/g, '') === query;

      if (onlySimple && query.length <= 3) {
        const bucket = this.iataIndex[query] || [];
        if (bucket.length) {
          this.suggestions = bucket.slice(0, this.maxSuggestions);
          this.highlightIndex = 0;
          return;
        }
      }

      for (let i = 0; i < this.index.length; i += 1) {
        if (this.index[i].searchText.includes(query)) {
          results.push(this.index[i].option);
          if (results.length >= this.maxSuggestions) break;
        }
      }
      this.suggestions = results;
      this.highlightIndex = results.length ? 0 : -1;
    }
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.buildTimer) clearTimeout(this.buildTimer);
    if (this.idleHandle && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.idleHandle);
    }
    if (this.buildIdleHandle && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.buildIdleHandle);
    }
  }
};
</script>

<style lang="scss" scoped>
.autocomplete {
  position: relative;
}

.autocomplete__list {
  position: absolute;
  z-index: 30;
  width: 100%;
  max-height: 260px;
  overflow: auto;
  background: #fff;
  border: 1px solid #E2E2E2;
  box-shadow: 0 6px 18px rgba(47,59,80,0.12);
  margin-top: 4px;
}

.autocomplete__item {
  padding: 8px 12px;
  font-size: 14px;
  color: #768093;
  cursor: pointer;
}

.autocomplete__item:hover,
.autocomplete__item.is-active {
  background: #f3f5f7;
}

.autocomplete__empty {
  padding: 10px 12px;
  font-size: 14px;
  color: #768093;
}

.autocomplete.has-error .autocomplete__input {
  border-color: #e24646;
}

.autocomplete__error {
  margin-top: 6px;
}
</style>
